import React, { useState, useEffect } from 'react';
import { reminderService } from '../services/reminderService';
import { FaCheckCircle, FaUndoAlt, FaPlusCircle, FaTimes, FaClock, FaCalendarAlt } from 'react-icons/fa';

function Reminder() {
  const [showForm, setShowForm] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [newReminder, setNewReminder] = useState({
    title: '',
    description: '',
    dueDate: '',
    isCompleted: false
  });

  useEffect(() => {
    loadReminders();
  }, []);

  const loadReminders = async () => {
    try {
      const response = await reminderService.getReminders();
      setReminders(response.data);
    } catch (error) {
      console.error('Error loading reminders:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await reminderService.createReminder(newReminder);
      loadReminders();
      setNewReminder({ title: '', description: '', dueDate: '', isCompleted: false });
      setShowForm(false);
    } catch (error) {
      console.error('Error creating reminder:', error);
    }
  };

  const toggleStatus = async (id) => {
    try {
      const reminder = reminders.find(r => r._id === id);
      if (!reminder) {
        console.error('Reminder not found:', id);
        return;
      }
      await reminderService.updateReminder(id, { ...reminder, isCompleted: !reminder.isCompleted });
      loadReminders();
    } catch (error) {
      console.error('Error updating reminder:', error);
    }
  };

  const formatTimeRemaining = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diff = due - now;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (diff < 0) {
      const overdueMins = Math.abs(minutes);
      if (overdueMins < 60) return `${overdueMins}m overdue`;
      if (overdueMins < 1440) return `${Math.floor(overdueMins/60)}h overdue`;
      return `${Math.floor(overdueMins/1440)}d overdue`;
    }

    if (minutes < 60) return 'Due in a few minutes';
    if (hours < 24) {
      if (hours === 1) return 'Due in 1 hour';
      return `Due in ${hours} hours`;
    }
    if (days === 1) return 'Due tomorrow';
    if (days < 7) return `Due in ${days} days`;
    return `Due on ${due.toLocaleDateString()}`;
  };

  const sortedReminders = [...reminders].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  const pendingReminders = sortedReminders.filter(r => !r.isCompleted);
  const completedReminders = sortedReminders.filter(r => r.isCompleted);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Reminders & Tasks</h1>
        <button 
          onClick={() => setShowForm(!showForm)} 
          className="inline-flex items-center justify-center p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-all duration-200 shadow-sm w-10"
          title={showForm ? "Cancel" : "New Task"}
        >
          {showForm ? <FaTimes className="text-base" /> : <FaPlusCircle className="text-base" />}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow-md mb-8">
          <input
            type="text"
            placeholder="Title"
            value={newReminder.title}
            onChange={(e) => setNewReminder({...newReminder, title: e.target.value})}
            required
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <textarea
            placeholder="Description"
            value={newReminder.description}
            onChange={(e) => setNewReminder({...newReminder, description: e.target.value})}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32"
          />
          <input
            type="date"
            value={newReminder.dueDate}
            onChange={(e) => setNewReminder({...newReminder, dueDate: e.target.value})}
            required
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button type="submit" className="w-full px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-all duration-200">
            Add Reminder
          </button>
        </form>
      )}

      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Pending Tasks ({pendingReminders.length})</h2>
          <div className="space-y-4">
            {pendingReminders.map(reminder => (
              <div key={reminder._id} className="bg-white rounded-lg shadow-md p-4 flex justify-between items-start">
                <div className="flex-1 mr-2">
                  <h3 className="font-semibold text-lg text-gray-800">{reminder.title}</h3>
                  <p className="text-gray-600 mt-1">{reminder.description}</p>
                  <div className="flex gap-4 mt-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <FaCalendarAlt className="text-blue-500" /> {new Date(reminder.dueDate).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaClock className="text-blue-500" /> {formatTimeRemaining(reminder.dueDate)}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 w-10">
                  <button 
                    onClick={() => toggleStatus(reminder._id)}
                    className="w-full inline-flex items-center justify-center p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-all duration-200"
                    title="Mark as complete"
                  >
                    <FaCheckCircle className="text-base" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Completed Tasks ({completedReminders.length})</h2>
          <div className="space-y-4">
            {completedReminders.map(reminder => (
              <div key={reminder._id} className="bg-gray-50 rounded-lg shadow-md p-4 flex justify-between items-start">
                <div className="flex-1 mr-2">
                  <h3 className="font-semibold text-lg text-gray-600">{reminder.title}</h3>
                  <p className="text-gray-500 mt-1">{reminder.description}</p>
                  <div className="flex gap-4 mt-2 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <FaCalendarAlt /> Due date was: {new Date(reminder.dueDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 w-10">
                  <button 
                    onClick={() => toggleStatus(reminder._id)}
                    className="w-full inline-flex items-center justify-center p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-all duration-200"
                    title="Reopen task"
                  >
                    <FaUndoAlt className="text-base" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Reminder;
